import React from 'react';
import { Button } from '@/components/ui/button';
import { MobileTooltip } from '@/components/ui/mobile-tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Wrench } from 'lucide-react';
import { TabbedBarryLayout } from '../knowledge/TabbedBarryLayout';
import { useUserLocation } from '@/hooks/use-user-location';
import { useProfile } from '@/hooks/profile';
import { useLocation } from 'react-router-dom';
import { useBarry } from '@/contexts/BarryContext';
import { SITE_IMAGES } from '@/config/images';

export function FloatingBarryButton() {
  const { location } = useUserLocation();
  const { userData } = useProfile();
  const routerLocation = useLocation();
  const { onWISAction, pageContext, isBarryOpen, openBarry, closeBarry } = useBarry();

  // Detect if we're on the WIS page
  const isWISPage = routerLocation.pathname.includes('/knowledge/wis');

  // Generate contextual tooltip based on current page
  const getContextualTooltip = () => {
    if (isWISPage) {
      return 'Barry WIS Assistant';
    }

    if (!pageContext) {
      return 'Chat with Barry - AI Mechanic';
    }

    switch (pageContext.pageName) {
      case 'marketplace':
        return 'Ask Barry about parts compatibility and installation';
      case 'dashboard':
        return 'Chat with Barry about your Unimog maintenance';
      case 'trips':
        return 'Ask Barry about route preparation and vehicle setup';
      case 'knowledge':
        return 'Search manuals with Barry - AI technical expert';
      case 'events':
        return 'Ask Barry about event preparation and gear recommendations';
      case 'community':
        return 'Ask Barry about Unimog community tips and advice';
      case 'vehicle-showcase':
        return 'Ask Barry about any vehicle in the Showroom';
      case 'vehicle-detail':
        return 'Ask Barry about this specific vehicle';
      default:
        return 'Chat with Barry - AI Mechanic';
    }
  };

  // Handle Barry button click - different behavior for WIS page
  const handleBarryClick = () => {
    if (isWISPage && onWISAction) {
      // On WIS page, trigger WIS integration
      onWISAction('activate_barry_mode');
    } else {
      // On other pages, show normal chat modal
      openBarry();
    }
  };

  return (
    <>
      {/* Floating Barry Button */}
      <div className="fixed right-5 z-40 bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] md:bottom-8 md:right-8">
        <MobileTooltip content={getContextualTooltip()}>
          <Button
            onClick={handleBarryClick}
            size="lg"
            className={`rounded-full h-14 w-14 p-0 shadow-lg border-2 border-white transition-colors ${
              isWISPage
                ? 'bg-military-green hover:bg-military-green/90'
                : 'bg-unimog-500 hover:bg-unimog-600'
            }`}
          >
            <div className="relative w-10 h-10">
              <picture>
                <source srcSet={SITE_IMAGES.barryAvatar} type="image/webp" />
                <img
                  src={SITE_IMAGES.barryAvatarFallback}
                  alt="Barry"
                  className="w-full h-full rounded-full object-cover"
                />
              </picture>
              <Wrench className="h-4 w-4 absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 text-unimog-500" />
            </div>
          </Button>
        </MobileTooltip>
      </div>

      {/* Barry AI Chat Modal - Only show on non-WIS pages */}
      {!isWISPage && (
        <Dialog open={isBarryOpen} onOpenChange={(open) => open ? openBarry() : closeBarry()}>
        <DialogContent className="w-full max-w-full md:max-w-7xl h-[95vh] md:h-[90vh] p-0 overflow-hidden flex flex-col gap-0">
          <DialogHeader className="p-4 md:p-6 pb-3 md:pb-4 flex-shrink-0 border-b">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative flex-shrink-0">
                <picture>
                  <source srcSet={SITE_IMAGES.barryAvatar} type="image/webp" />
                  <img
                    src={SITE_IMAGES.barryAvatarFallback}
                    alt="Barry the AI Mechanic"
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-unimog-500"
                  />
                </picture>
                <Wrench className="h-3 w-3 md:h-4 md:w-4 absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 text-unimog-500" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-base md:text-xl font-bold text-unimog-800 dark:text-unimog-200 truncate">
                  Barry - AI Mechanic with Manual Access
                </DialogTitle>
                <DialogDescription className="text-xs md:text-sm text-muted-foreground hidden sm:block">
                  Ask Barry about maintenance, repairs, or any technical questions - click manual citations to view PDFs
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {/* 30/70 Split: Barry Chat + Tabbed PDF Viewer */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <TabbedBarryLayout location={location} userModel={userData?.unimogModel} />
          </div>
        </DialogContent>
        </Dialog>
      )}
    </>
  );
}
